import { categories } from './seed'
import { expensesByCategory } from './trip'
import type { Expense, FuelKind } from './types'

/**
 * Die Rechnung hinter den beiden Ausgabendiagrammen: Ring und Balken.
 *
 * Hier steht alles, was sich ohne Bildschirm entscheiden lässt - welche
 * Kategorie welchen Betrag und welche Farbe hat, wie lang ein Ringsegment auf
 * dem Umfang wird und welchen Anteil ein Balken zeigt. Gezeichnet wird daraus
 * in components/ExpenseChart.tsx, benutzt von der Budgetansicht und vom Buch.
 *
 * Dass diese Rechnung hier liegt und nicht in einer der beiden Ansichten, ist
 * der Grund, warum Bildschirm und Papier dieselben Zahlen zeigen müssen: es
 * gibt nur eine Stelle, an der sie entstehen. Und weil sie ohne DOM auskommt,
 * lässt sie sich prüfen.
 *
 * Das Koordinatensystem des Rings ist ein Quadrat von 140 auf 140 mit dem
 * Mittelpunkt (70, 70). Es wird im Buch auf rund 45 mm gesetzt; eine Einheit
 * ist also knapp ein Drittel Millimeter.
 */

/** Radius der Ringmitte, in Einheiten des viewBox. */
export const RING_R = 52
/** Strichstärke des Rings. Der Ring reicht damit von r=40 bis r=64. */
export const RING_BREITE = 24
/** Mittelpunkt des Rings in beiden Achsen. */
export const RING_MITTE = 70
/** Umfang der Mittellinie: die Länge, auf der die Segmente verteilt werden. */
export const RING_UMFANG = 2 * Math.PI * RING_R
/**
 * Lücke zwischen zwei Segmenten, in Einheiten des Umfangs.
 *
 * Ohne sie laufen zwei ähnliche Farben ineinander - auf einer
 * Schwarzweisskopie sogar zwei beliebige. Rund ein drittel Millimeter im Buch,
 * schmal genug, dass ein Segment dadurch nicht kleiner aussieht, als es ist.
 */
export const RING_LUECKE = 1.5

/** Das Koordinatensystem des Balkens: hundert Einheiten breit, sechs hoch. */
export const BALKEN_BREITE = 100
export const BALKEN_HOEHE = 6

/** Die Farbe für alles, was in der Kategorienliste nicht mehr steht. */
const RESTFARBE = '#7a7266'

/** Eine Kategorie mit ihrer Summe und ihrer Farbe. */
export interface CategorySum {
  key: string
  value: number
  color: string
}

/** Ein Stueck des Rings, fertig zum Zeichnen als gestrichelter Kreis. */
export interface RingSegment {
  key: string
  color: string
  /** Sichtbare Strichlänge auf dem Umfang, die Lücke ist schon abgezogen. */
  strich: number
  /** Wo das Segment auf dem Umfang anfängt, ohne Lücke gerechnet. */
  versatz: number
}

/**
 * Die Ausgaben je Kategorie, absteigend, mit der Farbe der Kategorie.
 *
 * Kategorien, die in der Liste nicht mehr stehen, behalten ihre Summe und
 * bekommen die Restfarbe: eine umbenannte Kategorie soll aus dem Diagramm nicht
 * verschwinden, nur weil die Farbtabelle ihren alten Namen nicht mehr kennt.
 * Das gilt auch für die Kraftstoffkategorie einer Reise, die inzwischen mit
 * einem anderen Fahrzeug unterwegs ist - „Benzin“ neben „Strom“ ist eine
 * Auskunft und kein Fehler.
 *
 * @param fuel die Kraftstoffart der Reise; sie bestimmt, welche Kategorie die
 *   Kraftstofffarbe bekommt.
 */
export function expenseSums(expenses: readonly Expense[], fuel: FuelKind): CategorySum[] {
  const farben = new Map(categories(fuel).map((kategorie) => [kategorie.key, kategorie.color]))
  return expensesByCategory([...expenses]).map((eintrag) => ({
    ...eintrag,
    color: farben.get(eintrag.key) ?? RESTFARBE,
  }))
}

/** Summe über alle Kategorien - was unterm Strich ausgegeben wurde. */
export function sumTotal(summen: readonly CategorySum[]): number {
  return summen.reduce((summe, eintrag) => summe + eintrag.value, 0)
}

/**
 * Der Bezugswert der Zeichnung: die Summe dessen, was überhaupt Platz belegen
 * kann.
 *
 * Nicht sumTotal, und das ist der Unterschied, an dem sonst der Ring zerbricht.
 * Ein Betrag darf negativ sein - eine zurückerstattete Kaution, eine geteilte
 * Rechnung, die jemand zurückzahlt. Das Formular lässt das zwar nicht zu, der
 * Import und der Abgleich aber sehr wohl, und beide sollen es auch: eine
 * Erstattung ist Geld, das im Buch zu stehen hat, und sie beim Einlesen
 * wegzuwerfen hiesse, eine Zahl zu verlieren, um ein Bild zu retten.
 *
 * Nur zeichnen lässt sie sich nicht. Ein Ring zeigt Anteile an einem Ganzen,
 * und ein negativer Anteil ist keiner: mit sumTotal als Bezug wurde aus
 * +100, +100, -50 eine Strichsumme von 432,63 auf einem Umfang von 326,73 - das
 * zweite Segment lief über den Kreis hinaus und übermalte das erste, und der
 * Anteilsbalken schoss mit 200 Prozent aus seinem viewBox.
 *
 * Also zwei Zahlen mit zwei Aufgaben: sumTotal ist die Auskunft (und steht als
 * Betrag da, mit Vorzeichen), dieser Wert hier ist der Massstab. Eine Kategorie,
 * die unterm Strich negativ ist, belegt keinen Bogen und keinen Balken; ihr
 * Betrag steht trotzdem in der Liste, wo er hingehört.
 */
export function anteilBezug(summen: readonly CategorySum[]): number {
  return summen.reduce((summe, eintrag) => summe + Math.max(0, eintrag.value), 0)
}

/**
 * Der Ring als Segmentliste.
 *
 * Gezeichnet wird er später als gestrichelter Kreis: ein Segment je Kategorie,
 * länger oder kürzer, mit wachsendem Versatz. Das spart die Bogenmathematik
 * eines Tortenstücks und ergibt dieselbe Figur.
 *
 * Der Versatz wächst um die volle Länge, die sichtbare Strichlänge ist um
 * die Lücke kürzer. Sonst würde sich die Lücke über den Ring aufsummieren
 * und das letzte Segment sässe schief.
 *
 * @param bezug der Massstab, siehe anteilBezug. Eine Kategorie mit negativer
 *   Summe belegt keinen Bogen - sonst liefe der Ring über sich selbst hinaus.
 */
export function ringSegments(summen: readonly CategorySum[], bezug: number): RingSegment[] {
  let versatz = 0
  return summen.map((eintrag) => {
    const laenge = bezug > 0 ? (Math.max(0, eintrag.value) / bezug) * RING_UMFANG : 0
    const segment = {
      key: eintrag.key,
      color: eintrag.color,
      strich: Math.max(0, laenge - RING_LUECKE),
      versatz,
    }
    versatz += laenge
    return segment
  })
}

/**
 * Anteil eines Betrags am Bezugswert, in Prozent. Ohne Bezug ist er null.
 *
 * Zwischen null und hundert, und beides ist eine Grenze und keine Rundung: die
 * Zahl beschriftet einen Balken, dessen Breite in Einheiten des viewBox
 * angegeben wird. Ein negativer Anteil ergäbe ein Rechteck mit negativer
 * Breite, ein Anteil über hundert eines, das aus dem Bild läuft.
 */
export function anteilProzent(value: number, bezug: number): number {
  if (bezug <= 0) return 0
  return Math.min(100, Math.max(0, (value / bezug) * 100))
}
